import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Archive, Loader2, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Super-admin control for the tenant-level Auto-Archive Stale Estimates
 * policy. Live estimates idle longer than `auto_archive_stale_days` are
 * soft-archived by a backend sweep (closed-won / approved deals excluded).
 */
export const AutoArchiveSettingsCard = () => {
  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tenant-settings`, { withCredentials: true });
      const d = res.data?.auto_archive_stale_days || 0;
      setEnabled(d > 0);
      if (d > 0) setDays(d);
    } catch (e) {
      // non-fatal — defaults stay
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const value = enabled ? Math.max(1, parseInt(days, 10) || 0) : 0;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/tenant-settings`, { auto_archive_stale_days: value }, { withCredentials: true });
      toast.success(value > 0 ? `Auto-archive set to ${value} idle days` : 'Auto-archive disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setSweeping(true);
    try {
      const res = await axios.post(`${API_URL}/api/tenant-settings/run-archive-sweep`, {}, { withCredentials: true });
      const n = res.data?.archived_count ?? 0;
      if (!res.data?.enabled) {
        toast.info('Policy is disabled — enable & save a day threshold first');
      } else {
        toast.success(n > 0 ? `Archived ${n} stale estimate${n === 1 ? '' : 's'}` : 'No stale estimates found');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  return (
    <Card data-testid="auto-archive-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
            <Archive className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-sm text-[#0F2040]">Auto-Archive Stale Estimates</h3>
                <p className="text-xs text-slate-500">Soft-archive live estimates that have been idle too long. Won, approved, and confirmed deals are never auto-archived.</p>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                disabled={loading}
                data-testid="auto-archive-toggle"
              />
            </div>

            {enabled && (
              <div className="flex items-end gap-3 flex-wrap">
                <div className="space-y-1">
                  <Label htmlFor="stale-days" className="text-xs text-slate-600">Idle days before archiving</Label>
                  <Input
                    id="stale-days"
                    type="number"
                    min={1}
                    max={3650}
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    className="w-32"
                    data-testid="auto-archive-days-input"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button size="sm" onClick={save} disabled={saving || loading} data-testid="auto-archive-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Save policy
              </Button>
              <Button size="sm" variant="outline" onClick={runNow} disabled={sweeping || loading} data-testid="auto-archive-run-btn">
                {sweeping ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5 mr-1.5" />}
                Run sweep now
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AutoArchiveSettingsCard;
